import { forwardRef, Ref, useEffect, useState } from "react";
import Select, { ActionMeta, ActionMetaBase } from "react-select";
import AsyncSelect from 'react-select/async';
import CustomOption from "./CustomOption";
import { SelectOption } from "./types";
import CustomMultiValue from "./CustomMultiValue";

export type DropdownMultiSelectProps = {
  options?: SelectOption[];
  onChange: (selected: SelectOption[]) => void;
  allOptionInResults: boolean;
  selected: SelectOption[];
  includeAllOption?: boolean;
  // Optional async loader used when options should be fetched as the user types
  loadOptions?: (inputValue: string) => Promise<SelectOption[]>;
  cacheOptions?: boolean;
  placeholder?: string;
  selectedOptionsToShow?: number;
  inErrorState?: boolean;
};

/**
 * DropdownMultiSelect component allows users to select multiple options from a dropdown list.
 * 
 * @param {DropdownMultiSelectProps} props - The props for the DropdownMultiSelect component.
 * @returns {JSX.Element} The rendered DropdownMultiSelect component.
 */
const DropdownMultiSelect = forwardRef(({ options, onChange, allOptionInResults, selected, includeAllOption, loadOptions, cacheOptions, placeholder, selectedOptionsToShow = 3, inErrorState }: DropdownMultiSelectProps, ref: Ref<any>) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(selected);
  const allOption: SelectOption = { value: " ", label: "All" };
  const safeOptions = options || [];
  const useAll = includeAllOption !== false;
  const optionsWithAll: SelectOption[] = useAll ? [allOption, ...safeOptions] : [...safeOptions];

  const getControlStyles = (provided: any) => ({
    ...provided,
    backgroundColor: inErrorState ? '#FEF2F2' : '#F9FAFB',
    borderColor: inErrorState ? '#EF4444' : provided.borderColor,
    '&:hover': {
      borderColor: inErrorState ? '#EF4444' : provided.borderColor
    }
  });

  const createCustomMultiValue = (props: any) => {
    return <CustomMultiValue {...props} selectedOptionsToShow={selectedOptionsToShow} />;
  };

  useEffect(() => {
    if (allOptionsSelected(selected)) {
      setSelectedOptions(optionsWithAll);
      return;
    }

    // Enrich incoming selected items with labels from previous state
    // when used in async mode, incoming selected may only have values
    setSelectedOptions(prev => {
      const enriched = (selected || []).map(s => {
        // If incoming already has a proper label (different from value), keep it
        if (s.label && s.label !== s.value) return s;

        const fromPrev = prev.find(o => o.value.toUpperCase() === s.value.toUpperCase());
        const label = fromPrev?.label ?? s.label ?? s.value;
        return { value: s.value, label };
      });

      return enriched;
    });
  }, [selected]);

  /**
   * Clears the selected options.
   */
  const clearSelectedOptions = () => {
    setSelectedOptions([]);
    onChange([]);
  };

  /**
   * Filters the selected values based on the presence of the "All" option.
   * 
   * @param {SelectOption[]} options - The selected options.
   * @returns {SelectOption[]} The filtered selected options.
   */
  const getSelectedValues = (options: SelectOption[]) => {
    return allOptionInResults ? options : options.filter((item) => item.value !== allOption.value)
  };

  /**
   * Checks if all options are selected.
   * 
   * @param {readonly SelectOption[]} selected - The selected options.
   * @returns {boolean} True if all options are selected, false otherwise.
   */
  const allOptionsSelected = (selected: readonly SelectOption[]): boolean => {
    if (safeOptions.length === 0) {
      return false;
    }
    if (!useAll) return false;
    const allSelected = selected.length === optionsWithAll.length + 1;
    const allSelectedExceptAll = selected.filter((item) => item.value !== allOption.value).length + 1 === optionsWithAll.length;
    return allSelected || allSelectedExceptAll;
  };

  /**
   * Handles the selection of an option.
   * 
   * @param {readonly SelectOption[]} selected - The selected options.
   * @param {ActionMetaBase<SelectOption>} action - The action meta data.
   */
  const handleSelectOption = (selected: readonly SelectOption[], action: ActionMetaBase<SelectOption>) => {
    if (useAll && (action.option?.value === allOption.value || allOptionsSelected(selected))) {
      setSelectedOptions(optionsWithAll);
      onChange(getSelectedValues(optionsWithAll));
    } else {
      setSelectedOptions([...selected]);
      onChange(getSelectedValues([...selected]));
    }
  };

  /**
   * Handles the removal of an option.
   * 
   * @param {readonly SelectOption[]} selected - The selected options.
   * @param {ActionMetaBase<SelectOption>} action - The action meta data.
   */
  const handleRemoveOption = (selected: readonly SelectOption[], action: ActionMetaBase<SelectOption>) => {
    handleOptionRemove(selected, action.removedValue);
  };

  /**
   * Handles the deselection of an option.
   * 
   * @param {readonly SelectOption[]} selected - The selected options.
   * @param {ActionMetaBase<SelectOption>} action - The action meta data.
   */
  const handleDeselectOption = (selected: readonly SelectOption[], action: ActionMetaBase<SelectOption>) => {
    handleOptionRemove(selected, action.option);
  };

  /**
   * Handles the removal of an option.
   * 
   * @param {readonly SelectOption[]} selected - The selected options.
   * @param {SelectOption | null | undefined} option - The option to remove.
   */
  const handleOptionRemove = (selected: readonly SelectOption[], option: SelectOption | null | undefined) => {
    if (option?.value === allOption.value) {
      clearSelectedOptions();
    } else {
      const isAllSelected = selectedOptions.some(
        (item: SelectOption) => item.value === allOption.value
      );
      const newSelectedOptions: SelectOption[] = isAllSelected
        ? selected.filter(
          (item: SelectOption) => item.value !== allOption.value &&
            item.value !== option?.value
        )
        : [...selected];
      setSelectedOptions(newSelectedOptions);
      onChange(getSelectedValues(newSelectedOptions));
    }
  }

  const actionHandlers: { [key: string]: (selected: readonly SelectOption[], action: ActionMeta<SelectOption>) => void } = {
    "select-option": handleSelectOption,
    "deselect-option": handleDeselectOption,
    "clear": clearSelectedOptions,
    "remove-value": handleRemoveOption,
    "pop-value": handleDeselectOption,
    "set-value": handleSelectOption,
    "create-option": () => {/** NOOP */ }
  };

  const handleChange = (selected: readonly SelectOption[], action: ActionMeta<SelectOption>) => {
    actionHandlers[action.action](selected, action);
  };

  if (loadOptions) {
    return (
      <AsyncSelect
        ref={ref} //forwardRef to react-select
        options={safeOptions}
        cacheOptions={cacheOptions}
        loadOptions={async (input: string) => { return await loadOptions(input); }}
        isMulti
        components={{ Option: CustomOption, MultiValue: createCustomMultiValue }}
        closeMenuOnSelect={true}
        hideSelectedOptions={false}
        onChange={handleChange}
        value={selectedOptions}
        placeholder={placeholder ?? 'Type to search...'}
        styles={{ control: getControlStyles }}
      />
    );
  }

  return (
    <Select
      ref={ref} //forwardRef to react-select
      options={optionsWithAll}
      isMulti
      components={{
        Option: CustomOption,
        MultiValue: createCustomMultiValue
      }}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      onChange={handleChange}
      value={selectedOptions}
      placeholder={placeholder ?? 'Select...'}
      styles={{ control: getControlStyles }}
    />
  );
});

export default DropdownMultiSelect;
